/**
 * AnalysisTemplateEditorTabs — the platform catalogue and the tab bodies of the analysis-template editor.
 * Demo page: `/form-layouts` (stacked labelled fields, two-column pairs). The fields are bound to the editor's
 * `<Form>` (see AnalysisTemplateEditor.tsx).
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { listIntelPlatformsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { IntelAnalysisTemplate } from '../../generated/api/types.gen.js';
import { Badge, Callout, Card, DataTable } from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import { SelectFormField, TextAreaFormField, TextFormField } from '../../components/form';
import { cn } from '../../utils/cn.js';

// ---------------------------------------------------------------------------
// Platform constants & mapping (exported for the editor)
// ---------------------------------------------------------------------------

export const PLATFORMS: Array<NonNullable<IntelAnalysisTemplate['platform']>> = [
  'youtube',
  'twitter',
  'reddit',
  'telegram',
  'discord',
  'tradingview',
  'medium',
  'substack',
  'podcast',
  'wallet',
  'smart_money',
  'exchange_wallet',
  'dao_treasury',
  'lending',
  'dex',
  'perp_dex',
  'options',
  'yield_aggregator',
  'governance',
  'etf',
  'hedge_fund',
  'market_maker',
  'vc',
  'research_firm',
  'analytics',
  'data_aggregator',
  'cex',
  'prediction_market',
  'news_wire',
];

export const platformToCategory: Record<string, NonNullable<IntelAnalysisTemplate['category']>> = {
  youtube: 'social',
  twitter: 'social',
  reddit: 'social',
  telegram: 'social',
  discord: 'social',
  tradingview: 'social',
  medium: 'social',
  substack: 'social',
  podcast: 'social',
  wallet: 'on_chain',
  smart_money: 'on_chain',
  exchange_wallet: 'on_chain',
  dao_treasury: 'on_chain',
  lending: 'defi',
  dex: 'defi',
  perp_dex: 'defi',
  options: 'defi',
  yield_aggregator: 'defi',
  governance: 'defi',
  etf: 'institutional',
  hedge_fund: 'institutional',
  market_maker: 'institutional',
  vc: 'institutional',
  research_firm: 'research',
  analytics: 'research',
  data_aggregator: 'research',
  cex: 'market_infra',
  prediction_market: 'market_infra',
  news_wire: 'market_infra',
};

const words = (value: string) => {
  const spaced = value.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const PLATFORM_OPTIONS = PLATFORMS.map((p) => ({ value: p, label: words(p) }));

// ---------------------------------------------------------------------------
// Details tab
// ---------------------------------------------------------------------------

export const DetailsTab: React.FC<{ platform: string; disabled: boolean }> = ({ platform, disabled }) => {
  const category = platformToCategory[platform] ?? 'social';
  const platforms = useQuery(listIntelPlatformsOptions());
  const connections = (platforms.data?.data ?? []).filter((p) => p.slug === platform);

  return (
    <div className="grid max-w-2xl grid-cols-1 gap-5">
      <TextFormField name="name" label="Name" placeholder="Sentiment extraction" required disabled={disabled} />
      <TextAreaFormField
        name="description"
        label="Description"
        placeholder="What this template does…"
        rows={3}
        disabled={disabled}
      />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <SelectFormField name="platform" label="Platform" options={PLATFORM_OPTIONS} required disabled={disabled} />
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-input-label-text">Category</span>
          <div className="flex h-[42px] items-center gap-2">
            <Badge variant="info">{words(category)}</Badge>
            <span className="text-xs text-muted">Set by the platform</span>
          </div>
        </div>
      </div>

      <TextFormField
        name="tagsText"
        label="Tags"
        placeholder="sentiment, social"
        description="Separate tags with commas."
        disabled={disabled}
      />

      {platforms.isError && (
        <Callout tone="warning" title="Platform connection unknown">
          The platform list did not load, so this cannot show whether the platform is connected.
        </Callout>
      )}

      {connections.length > 0 && (
        <div>
          <p className="mb-1.5 text-sm font-medium text-input-label-text">Platform connection</p>
          <ul className="space-y-1 rounded-lg border border-card-border bg-surface-alt p-3">
            {connections.map((p) => (
              <li key={p.slug} className="flex items-center justify-between text-sm">
                <span className="font-medium text-body">{p.name}</span>
                <Badge variant={p.enabled ? 'success' : 'neutral'} dot size="sm">
                  {p.enabled ? 'Connected' : 'Disabled'}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Prompt tab
// ---------------------------------------------------------------------------

export const PromptTab: React.FC<{ length: number; disabled: boolean }> = ({ length, disabled }) => (
  <div className="max-w-3xl space-y-3">
    <TextAreaFormField
      name="promptTemplate"
      label="Instructions"
      placeholder="Analyze the following content and provide…"
      rows={18}
      required
      disabled={disabled}
      className={cn('[&_textarea]:font-mono [&_textarea]:text-xs')}
      description={`${length.toLocaleString()} characters. This prompt is added to the parent template's system prompt when a workflow runs. Use {{variable}} placeholders for values that change per run.`}
    />
  </div>
);

// ---------------------------------------------------------------------------
// Contract tab (read-only: the create and update routes do not accept variables or constraints)
// ---------------------------------------------------------------------------

type TemplateVariable = NonNullable<IntelAnalysisTemplate['variables']>[number];

const VARIABLE_COLUMNS: DataTableColumn<TemplateVariable>[] = [
  { key: 'name', header: 'Variable', render: (v) => <span className="font-mono text-xs">{v.name}</span> },
  { key: 'description', header: 'Description', render: (v) => <span className="text-muted">{v.description}</span> },
  { key: 'default', header: 'Default', render: (v) => v.default ?? <span className="text-faint">—</span> },
];

export const ContractTab: React.FC<{ template: IntelAnalysisTemplate | undefined }> = ({ template }) => {
  const variables = template?.variables ?? [];
  const constraints = template?.constraints ?? '';

  return (
    <div className="max-w-3xl space-y-5">
      <Callout tone="accent" title="Read-only">
        Variables and constraints are part of the template definition and cannot be changed from this editor.
      </Callout>
      <Card title="Variables">
        <p className="mb-3 text-sm text-muted">Values that can be substituted into the prompt when a workflow runs.</p>
        <DataTable
          columns={VARIABLE_COLUMNS}
          rows={variables}
          rowKey={(v, i) => v.name || `variable-${i}`}
          aria-label="Template variables"
          empty={<p className="py-6 text-center text-sm text-muted">This template defines no variables.</p>}
        />
      </Card>
      <Card title="Constraints">
        {constraints ? (
          <pre className="whitespace-pre-wrap font-mono text-xs text-body">{constraints}</pre>
        ) : (
          <p className="text-sm text-muted">This template defines no constraints.</p>
        )}
      </Card>
    </div>
  );
};
